import { Authentication, Service } from '@tino-frontend/adapters'
import {
  Auth,
  PasswordResponse,
  SignUpPayload,
  VerifyEmailResponse,
  SignUpFeedbackStatus,
  VerifyPhoneResponse,
  VerifiedPhoneResponse,
  LoginPhoneCampaingLocalStorage,
  SignUpPayloadForMerchant,
} from '@types'
import { SignUpService } from './sign-up.service'
import { REACT_APP_SUPPLIER_ID_DEFAULT } from 'constants/envs'

export class AuthService {
  user: Auth = {
    displayName: '',
    email: '',
    photoURL: '',
    uid: '',
    token: '',
  }
  private blockRedirect = false
  private blockAnonymous = false
  private interceptorId: number[] = []

  constructor(
    private readonly auth: Authentication<unknown, unknown>,
    private readonly http: Service[],
    private readonly signUpService: SignUpService,
    private readonly authAPI: Service,
    private readonly storage: Service
  ) {
    /// add logout on window domain to help on e2e tests
    ;(window as any).e2eLogout = () => this.signOut()
  }

  async signInWithCustomToken(customToken: string) {
    const user = await this.auth.signInWithCustomToken(customToken)
    this.setInterceptor()
    return user
  }

  async signIn(email: string, password: string) {
    const user = await this.auth.signInWithEmailAndPassword(email, password)
    this.setInterceptor()
    return user
  }

  async signUp(payload: SignUpPayload, verifyQSA: boolean): Promise<Auth> {
    this.blockRedirect = true

    const { exists } = await this.verifyEmail(payload.email)

    if (exists) {
      throw new Error(SignUpFeedbackStatus.EMAIL_EXISTS)
    }

    // TODO: Colocarmos isso em um Promise.all junto do verifyEmail quando isso virar um fluxo default
    const { exists: phoneExists } = await this.verifyPhone(
      payload.phone.slice(2)
    )

    if (phoneExists) {
      throw new Error(SignUpFeedbackStatus.PHONE_EXISTS)
    }

    const preSignUpResponse = await this.signUpService.preSignUp(payload)

    if (preSignUpResponse.newly_founded) {
      throw new Error(SignUpFeedbackStatus.NEWLY_FOUNDED)
    }

    if (verifyQSA) {
      const { is_owner } = await this.signUpService.verifyDocumentNumber(
        payload.email,
        payload.natural_person_document_number
      )

      if (!is_owner) {
        throw new Error(SignUpFeedbackStatus.NOT_OWNER)
      }
    }

    const user = await this.auth.createUserWithEmailAndPassword(
      payload.email,
      payload.password
    )
    this.setInterceptor()

    await this.signUpService.createOnboardingUser({
      email: payload.email,
      name: payload.name,
      phone: payload.phone,
      supplier_id:
        payload.supplier_id ||
        sessionStorage.getItem('supplier_id') ||
        REACT_APP_SUPPLIER_ID_DEFAULT,
      natural_person_document_number: payload.natural_person_document_number,
      legal_person_document_number: payload.company_document_number,
      terms: payload.terms,
    })

    return user
  }

  async registerMerchantBySalesperson(
    payload: SignUpPayloadForMerchant
  ): Promise<{ success: boolean; message: string }> {
    this.blockRedirect = true

    const { exists } = await this.verifyEmail(payload.email)
    if (exists) {
      throw new Error(SignUpFeedbackStatus.EMAIL_EXISTS)
    }

    const { exists: phoneExists } = await this.verifyPhone(
      payload.phone.slice(2)
    )
    if (phoneExists) {
      throw new Error(SignUpFeedbackStatus.PHONE_EXISTS)
    }

    this.setInterceptor()

    try {
      await this.signUpService.createMerchantUserBySalesperson({
        email: payload.email,
        natural_person_name: payload.name,
        phone_number: payload.phone,
        supplier_company_id:
          payload.supplier_id ||
          sessionStorage.getItem('supplier_id') ||
          REACT_APP_SUPPLIER_ID_DEFAULT,
        natural_person_document_type: 'CPF',
        legal_person_document_type: 'CNPJ',
        natural_person_document_number: payload.natural_person_document_number,
        legal_person_document_number: payload.company_document_number,
        identity_validated_by_salesperson:
          payload.identity_validated_by_salesperson || false,
      })

      return { success: true, message: 'User registered successfully' }
    } catch (error) {
      const errorMessage =
        error instanceof Error && error.message
          ? error.message
          : 'Registration failed due to an unknown error.'
      return { success: false, message: errorMessage }
    }
  }

  async registerMerchantBySupplier(
    payload: SignUpPayloadForMerchant
  ): Promise<{ success: boolean; message: string }> {
    this.blockRedirect = true

    const { exists } = await this.verifyEmail(payload.email)
    if (exists) {
      throw new Error(SignUpFeedbackStatus.EMAIL_EXISTS)
    }

    const { exists: phoneExists } = await this.verifyPhone(
      payload.phone.slice(2)
    )
    if (phoneExists) {
      throw new Error(SignUpFeedbackStatus.PHONE_EXISTS)
    }

    this.setInterceptor()

    try {
      await this.signUpService.createMerchantUserBySupplier({
        email: payload.email,
        natural_person_name: payload.name,
        phone_number: payload.phone,
        supplier_company_id:
          payload.supplier_id ||
          sessionStorage.getItem('supplier_id') ||
          REACT_APP_SUPPLIER_ID_DEFAULT,
        natural_person_document_type: 'CPF',
        legal_person_document_type: 'CNPJ',
        natural_person_document_number: payload.natural_person_document_number,
        legal_person_document_number: payload.company_document_number,
        identity_validated_by_salesperson:
          payload.identity_validated_by_salesperson || false,
      })

      return { success: true, message: 'User registered successfully' }
    } catch (error) {
      const errorMessage =
        error instanceof Error && error.message
          ? error.message
          : 'Registration failed due to an unknown error.'
      return { success: false, message: errorMessage }
    }
  }

  getCurrentUser() {
    return this.auth.getCurrentUser()
  }

  async signOut() {
    await this.auth.signOut()
    this.removeInterceptor()
  }

  sendPasswordResetEmail(email: string): Promise<PasswordResponse> {
    return this.auth.sendPasswordResetEmail(email).then(() => ({ email }))
  }

  confirmPasswordReset(code: string, password: string) {
    return this.auth.confirmPasswordReset(code, password)
  }

  onAuthStateChange(callback: (e: Auth | null) => void) {
    return this.auth.onIdTokenChanged(async (e: any, u) => {
      const { isAnonymous } = e || { isAnonymous: false }
      this.setInterceptor()
      if (!this.blockRedirect) {
        callback({ ...u, isAnonymous })
      }
      if (!e && !this.blockAnonymous) {
        this.blockAnonymous = true
        this.auth.signInAnonymously()
      }
    })
  }

  reAuthenticated(currentPassword: string) {
    return this.auth.reauthenticateWithCredential(currentPassword)
  }

  async checkRecoveryCode(code: string) {
    return this.auth.checkActionCode(code)
  }

  async recoveryPassword(code: string, newPassword: string) {
    return this.auth.recoveryPassword(code, newPassword)
  }

  async updatePassword(currentPassword: string, newPassword: string) {
    return this.auth.updatePassword(currentPassword, newPassword)
  }

  signInAnonymously() {
    return this.auth.signInAnonymously()
  }

  removeInterceptor() {
    if (this.interceptorId.length) {
      this.http.forEach((item, index) =>
        item.removeInterceptorRequest(this.interceptorId[index] as number)
      )

      return this.http
    }
  }

  async setInterceptor() {
    await Promise.all(
      this.http.map(
        async (adapter, index) =>
          (this.interceptorId[index] =
            await adapter.setInterceptorRequestHeaders(async (config: any) => {
              try {
                const token = await this.auth.getIdToken()
                if (token) {
                  config.headers.Authorization = `Bearer ${token}`
                }
                return config
              } catch (e) {
                return config
              }
            }))
      )
    )

    return this.interceptorId
  }

  async verifyEmail(email: string): Promise<VerifyEmailResponse> {
    return this.authAPI
      .get<
        unknown,
        {
          exists_on_core: boolean
          exists_on_user_auth: boolean
          exists_on_identity_platform: boolean
        }
      >(`/v1/emails/${email}/valid`)
      .then((res) => ({
        exists: res.exists_on_identity_platform,
        preRegistered: res.exists_on_core,
      }))
  }

  async verifyPhone(phone: string): Promise<VerifyPhoneResponse> {
    return this.authAPI
      .get<
        unknown,
        { exists_on_user_auth: boolean }
      >(`/v1/phones/+55${phone}/valid`)
      .then((res) => ({
        exists: res.exists_on_user_auth,
      }))
  }

  async verifyAuthenticatedPhone(): Promise<VerifiedPhoneResponse> {
    return this.authAPI.get<unknown, VerifiedPhoneResponse>(
      '/v1/phones/verified'
    )
  }

  deleteRememberLoginPhoneCampaingDate() {
    localStorage.removeItem('remember-login-phone-campaing-date')
  }

  saveRememberLoginPhoneCampaingDate(): Promise<void> {
    return this.storage.post('remember-login-phone-campaing-date')
  }

  getRememberLoginPhoneCampaingDate():
    | LoginPhoneCampaingLocalStorage
    | undefined {
    const value = localStorage.getItem('remember-login-phone-campaing-date')
    return value ? JSON.parse(value) : undefined
  }
}
